import type { ReactNode } from "react";
import type { ReportPdfModel } from "./reportTypes";
import {
  AboutGowitek,
  Framework,
  Interpretation,
  Matrix,
  NextNinety,
  Opportunities,
  Outcomes,
  Positioning,
  Risks,
  Roadmap,
  SignalBreakdown,
  SignalInputs,
  Snapshot,
} from "./reportSections";

export type PdfContentUnit = {
  id: string;
  node: ReactNode;
  /** Optional PdfPage class when this unit is alone on a page */
  pageClassName?: string;
  /** Stretch sole-section content to fill the A4 page (cover-style sections only) */
  fillPage?: boolean;
};

export function buildPdfContentUnits(model: ReportPdfModel): PdfContentUnit[] {
  const flow = { variant: "pdf" as const, stacked: true as const };

  return [
    {
      id: "snapshot",
      pageClassName: "pdf-page-snapshot",
      fillPage: true,
      node: (
        <Snapshot
          {...flow}
          overallScore={model.overallScore}
          readiness={model.snapshot.readiness}
          fastestRoiArea={model.snapshot.fastestRoiArea}
          pilotReadiness={model.snapshot.pilotReadiness}
          opportunityWindow={model.snapshot.opportunityWindow}
          executivePhrase={model.snapshot.executivePhrase}
          benchmarkCopy={model.snapshot.benchmarkCopy}
          bulletSignals={model.snapshot.bulletSignals}
        />
      ),
    },
    {
      id: "signal-breakdown",
      node: (
        <SignalBreakdown scores={model.breakdownScores} notes={model.breakdownNotes} {...flow} />
      ),
    },
    {
      id: "interpretation",
      node: (
        <Interpretation
          interpretationCopy={model.interpretationCopy}
          {...flow}
          compactObservation
        />
      ),
    },
    {
      id: "opportunities",
      node: <Opportunities cards={model.opportunityCards} {...flow} />,
    },
    {
      id: "matrix",
      pageClassName: "pdf-page-matrix",
      node: <Matrix roiPrioritization={model.roiPrioritization} {...flow} />,
    },
    {
      id: "roadmap",
      node: <Roadmap phases={model.executionPhases} {...flow} />,
    },
    {
      id: "outcomes",
      node: (
        <Outcomes
          expectedOutcomes={model.expectedOutcomes}
          {...flow}
          compactSummary
        />
      ),
    },
    {
      id: "positioning",
      node: <Positioning industryPositioning={model.industryPositioning} {...flow} />,
    },
    {
      id: "risks",
      node: <Risks risks={model.strategicRisks} {...flow} spacious />,
    },
    {
      id: "framework",
      node: <Framework {...flow} diagramSize={400} />,
    },
    ...buildNextNinetyPdfUnits(model, flow),
    {
      id: "about",
      node: <AboutGowitek {...flow} />,
    },
    {
      id: "signal-inputs",
      node: (
        <SignalInputs assessmentSignalInputs={model.assessmentSignalInputs} {...flow} />
      ),
    },
  ];
}

function buildNextNinetyPdfUnits(
  model: ReportPdfModel,
  flow: { variant: "pdf"; stacked: true },
): PdfContentUnit[] {
  const sectionEyebrow = model.recommendedImmediateActions?.section_eyebrow;
  const sectionTitle = model.recommendedImmediateActions?.section_title;
  const rawItems = model.recommendedImmediateActions?.items || [];
  const items = rawItems.filter(
    (item) => Boolean(item.time_label && item.title && item.description),
  );

  if (items.length === 0) {
    return [
      {
        id: "next-ninety",
        node: (
          <NextNinety
            {...flow}
            sectionEyebrow={sectionEyebrow}
            sectionTitle={sectionTitle}
            items={rawItems}
          />
        ),
      },
    ];
  }

  const itemsPerChunk = 2;
  const chunks: typeof items[] = [];
  for (let index = 0; index < items.length; index += itemsPerChunk) {
    chunks.push(items.slice(index, index + itemsPerChunk));
  }

  return chunks.map((chunkItems, chunkIndex) => ({
    id: `next-ninety-${chunkIndex}`,
    node: (
      <NextNinety
        {...flow}
        sectionEyebrow={sectionEyebrow}
        sectionTitle={sectionTitle}
        items={chunkItems}
        showHeader={chunkIndex === 0}
        continuation={chunkIndex > 0}
        itemOffset={chunkIndex * itemsPerChunk}
      />
    ),
  }));
}
